import { motion } from "framer-motion";
import { Heart, Star, Timer, Shuffle, Trophy, Volume2, VolumeX, Play } from "lucide-react";
import { getBest, TIMER_MULTIPLIER } from "../../game/config";
import { Switch } from "../ui/switch";
import { CustomSettings } from "./CustomSettings";
import { CategoryPicker } from "./CategoryPicker";

const ModeCard = ({ mode, selected, onSelect, i }) => (
  <motion.button
    data-testid={`mode-${mode.id}-btn`}
    onClick={() => onSelect(mode.id)}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.15 + i * 0.1 }}
    whileHover={{ y: -6 }}
    whileTap={{ scale: 0.97 }}
    className={`group relative overflow-hidden rounded-3xl border p-5 text-left backdrop-blur-xl transition-colors ${selected ? "border-white/60 bg-white/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
  >
    <div className={`absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${mode.accent} opacity-40 blur-2xl transition-opacity group-hover:opacity-70`} />
    <div className="relative">
      <span className={`inline-block rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-widest ${mode.badge}`}>{mode.name}</span>
      <p className="mt-3 text-sm text-white/70">{mode.tagline}</p>
      <ul className="mt-4 space-y-2 text-sm text-white/85">
        <li className="flex items-center gap-2"><Heart className="h-4 w-4 fill-[#ff2a6d] text-[#ff2a6d]" />{mode.lives} {mode.lives === 1 ? "life" : "lives"}</li>
        <li className="flex items-center gap-2"><Star className="h-4 w-4 fill-yellow-300 text-yellow-300" />{mode.bombs} star bombs</li>
        <li className="flex items-center gap-2"><Timer className="h-4 w-4 text-cyan-300" />{mode.id === "custom" ? `${mode.numbers} numbers · ${mode.memorize}s timer` : mode.timerDefault ? `${mode.memorize}s memorize timer` : "No timer by default"}</li>
        <li className="flex items-center gap-2"><Shuffle className="h-4 w-4 text-violet-300" />{mode.shuffle === "full" ? "Shuffles every round" : "Barely moves"}</li>
      </ul>
      <div className="mt-4 space-y-1 text-xs text-white/60">
        <div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-[#ffb703]" /> Best (timer): <span data-testid={`mode-${mode.id}-best-timer`} className="font-bold text-white">{getBest(mode.id, true)}</span></div>
        <div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-white/40" /> Best (no timer): <span data-testid={`mode-${mode.id}-best-notimer`} className="font-bold text-white">{getBest(mode.id, false)}</span></div>
      </div>
    </div>
  </motion.button>
);

const TimerToggle = ({ mode, timerOn, onToggle }) => (
  <div className="mt-6 flex flex-wrap items-center gap-4 rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl sm:p-5">
    <Switch data-testid="timer-toggle" checked={timerOn} onCheckedChange={onToggle} className="data-[state=checked]:bg-cyan-400" />
    <div className="flex-1">
      <div className="font-display font-bold text-white">
        Memorize timer {timerOn ? `on · ${mode.memorize}s` : "off · flip when ready"}
      </div>
      <div className="text-sm text-white/60">Timer on doubles your points. Faster solves, more hearts left and flawless rounds earn bonus points. Records are kept separately for timer on and off.</div>
    </div>
    <span data-testid="points-multiplier" className={`rounded-full px-3 py-1 font-display font-black ${timerOn ? "bg-amber-400 text-slate-900" : "bg-white/10 text-white/60"}`}>×{timerOn ? TIMER_MULTIPLIER : 1}</span>
  </div>
);

export const ModeSelect = ({ modes, selected, onSelect, onStart, timerOn, onTimerToggle, customCfg, onCustomChange, category, onCategoryChange, muted, onMute }) => (
  <div className="mx-auto flex min-h-screen max-w-5xl flex-col justify-center px-5 py-10 sm:px-8">
    <div className="flex items-start justify-between gap-4">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <div className="mb-4 flex gap-2">
          {[1, 2, 3].map((n, i) => (
            <motion.span key={n} animate={{ rotateY: [0, 180, 360] }} transition={{ delay: 0.6 + i * 0.2, duration: 0.8, repeat: Infinity, repeatDelay: 3 }}
              className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-sky-400 to-indigo-500 font-display text-xl font-black text-white shadow-lg">{n}</motion.span>
          ))}
        </div>
        <h1 className="font-display text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
          Number Memory <span className="bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-amber-300 bg-clip-text text-transparent">Challenge</span>
        </h1>
        <p className="mt-4 max-w-xl text-base text-white/70 md:text-lg">Memorize the tiles, wait for the flip, then find them in order — 1, 2, 3 or A, B, C, or follow the sequence strip. Dodge the stars — they cost 2 lives!</p>
      </motion.div>
      <button data-testid="mute-toggle-btn" onClick={onMute} className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-white/10 bg-white/5 text-white/80 transition-colors hover:bg-white/15" aria-label="Toggle sound">
        {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </button>
    </div>
    <CategoryPicker selected={category} onSelect={onCategoryChange} />
    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Object.values(modes).map((m, i) => <ModeCard key={m.id} mode={m} i={i} selected={selected === m.id} onSelect={onSelect} />)}
    </div>
    {selected === "custom" && <CustomSettings cfg={customCfg} onChange={onCustomChange} />}
    <TimerToggle mode={modes[selected]} timerOn={timerOn} onToggle={onTimerToggle} />
    <motion.button
      data-testid="start-game-btn"
      onClick={onStart}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      className="mt-8 inline-flex items-center gap-3 self-start rounded-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-amber-400 px-8 py-4 font-display text-lg font-black text-white shadow-[0_10px_40px_rgba(217,70,239,0.45)]"
    >
      <Play className="h-5 w-5 fill-white" /> Start {modes[selected].name} Game
    </motion.button>
  </div>
);
